import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { MAX_CART_ITEM_QUANTITY } from '../constants';
import { addLoyaltiesCode, clearCart } from '../reducers/cartSlice';
import { addHypothesisItemToCart } from '../reducers/menuSlice';
import { orderActions } from '../reducers/orderSlice';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import {
  selectFinalOrderSubmitted,
  selectSessionId,
} from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectHypothesisFrame } from '../selectors/message';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { EntityMenuItem } from '../reducers/messagingSlice';
import { convertBMOrderItemToEntityItem } from '../utils/menuUnification.utils';
import { CartItemAdditionTypes } from '../constants/event';
import { ICartLoyalty } from '../reducers/cartSlice.props';
import { selectBrandMenuConstraints } from '../selectors/menu';

const useHypothesisFrames = () => {
  const dispatch = useAppDispatch();
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const hypothesisFrame = useAppSelector(selectHypothesisFrame);
  const isFinalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const sbSessionId = useAppSelector(selectSessionId);
  const brandMenuConstraints = useAppSelector(selectBrandMenuConstraints);

  const shouldAddHypothesisItems =
    isAIAutoMode &&
    hypothesisFrame &&
    hypothesisFrame.session_id === sbSessionId;

  const hitlBrandMenuMode = getMenuUnificationFeatureFlag();

  useEffect(() => {
    if (!isFinalOrderSubmitted && shouldAddHypothesisItems) {
      hypothesisFrame?.data?.hypotheses?.forEach((hypothesis) => {
        const {
          order_items: orderItems,
          brand_menu_order_items: brandMenuOrderItems,
          is_brand_menu: aiBrandMenuMode,
          loyalties = [],
          customer_info,
        } = hypothesis;

        dispatch(clearCart()); // Clear the cart and build a new one to avoid the comparison between cart items and order items when hypothesis evolves(update quantity or modifiers)
        dispatch(orderActions.setCustomerName('')); // Reset the customer name
        let skipFindInMenu = false;
        let orderItemsToUse: EntityMenuItem[] = orderItems || [];

        // Use brand menu cart:
        // 1. if both AI and HITL are using brand menu
        // 2. if pos cart is not available when AI uses brand menu but HITL uses PRP menu
        if (aiBrandMenuMode && hitlBrandMenuMode) {
          orderItemsToUse = convertBMOrderItemToEntityItem(
            brandMenuOrderItems || [],
            '',
            brandMenuConstraints
          );
        } else if (aiBrandMenuMode && !hitlBrandMenuMode) {
          // Skip find the hypothesis order item in the menu when AI and HITL are using different menu
          skipFindInMenu = true;
          if (orderItems && orderItems.length === 0) {
            // Use brand menu cart
            orderItemsToUse = convertBMOrderItemToEntityItem(
              brandMenuOrderItems || [],
              '',
              brandMenuConstraints
            );
          }
        }

        //Add loyalties added by AI in HITL cart
        loyalties.forEach((loyalty: ICartLoyalty) => {
          dispatch(
            addLoyaltiesCode({
              code: loyalty.code,
              addedBy: CartItemAdditionTypes.AI,
            })
          );
        });

        // Add customer info from AI hypothesis
        if (customer_info?.name) {
          dispatch(orderActions.setCustomerName(customer_info.name));
        }
        // Add items ordered by AI in HITL cart
        orderItemsToUse.forEach((orderItem) => {
          const { quantity } = orderItem;
          const transformedQuantity = Math.floor(quantity); //Added for temporary purpose only as quantity will never be in decimals

          dispatch(
            addHypothesisItemToCart({
              orderItem: {
                ...orderItem,
                quantity: Math.min(transformedQuantity, MAX_CART_ITEM_QUANTITY),
              },
              skipFindInMenu,
            })
          );
        });
      });
    }
  }, [
    hypothesisFrame,
    isFinalOrderSubmitted,
    shouldAddHypothesisItems,
    hitlBrandMenuMode,
  ]);
};

export default useHypothesisFrames;
